/**
 * Opens a WhatsApp URL reliably across platforms.
 *
 * The historic implementation pre-opened `about:blank` in a new tab so it
 * could navigate that tab to `wa.me/...` after an `await` resolved (Safari
 * kills `window.open()` calls that happen after an async boundary). That
 * works on iOS because `wa.me` is a universal link and Safari hands it off
 * to the WhatsApp app from any tab. **But on Android Chrome the new-tab
 * trick prevents the WhatsApp app intent from firing** — the user instead
 * lands on the `wa.me` web page which shows a "Download WhatsApp" CTA.
 *
 * Fix:
 *   - On mobile (iOS and Android): navigate the **current tab** to `wa.me`.
 *     Both platforms then correctly hand off to the installed WhatsApp app
 *     (universal link on iOS, Intent on Android Chrome). No new tab.
 *   - On desktop: keep the about:blank-then-navigate trick so the chat
 *     opens in a new tab and the storefront stays open behind it.
 *
 * Usage (unchanged):
 *   const popup = openWhatsappWindow();          // synchronous, in click handler
 *   ... await backend ...
 *   popup.navigate("https://wa.me/...");          // may be async
 */

/**
 * Canonical Luxsova WhatsApp number — driven by REACT_APP_WHATSAPP_NUMBER
 * so future brand switches only touch `.env`. Falls back to Luxsova's
 * live number if the env is unset during dev.
 */
export const WHATSAPP_NUMBER =
  (typeof process !== "undefined" && process.env?.REACT_APP_WHATSAPP_NUMBER) ||
  "918718884698";

/**
 * Build a full `https://wa.me/…?text=…` URL with the shared number and
 * a URL-encoded prefill message. Zero-arg call returns the plain link.
 */
export function whatsappUrl(message = "") {
  const base = `https://wa.me/${WHATSAPP_NUMBER}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}
const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|Opera Mini/i.test(ua);
};

export function openWhatsappWindow() {
  const mobile = isMobile();
  let win = null;

  if (!mobile) {
    // Desktop only — pre-open a blank tab so the popup blocker treats the
    // later navigation as user-initiated.
    try {
      win = window.open("about:blank", "_blank");
    } catch {
      win = null;
    }
  }

  return {
    navigate(url) {
      if (!url) return;
      if (mobile) {
        // Same-tab navigation — Android Chrome + iOS Safari both correctly
        // hand off `wa.me` to the installed WhatsApp app from this.
        window.location.href = url;
        return;
      }
      if (win && !win.closed) {
        try {
          win.location.href = url;
          return;
        } catch {
          // popup blocked / cross-origin write — fall through to same-tab
        }
      }
      window.location.href = url;
    },
    abort() {
      if (win && !win.closed) {
        try {
          win.close();
        } catch {
          // ignore
        }
      }
    },
  };
}
